import { c as _c } from "react/compiler-runtime";
import { Box, Stack, type SxProps, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { ArgumentType } from "#/entities/argument/model/argumentObject";
import { type ArrayData, arrayDataItemSelectors } from "#/entities/dataStructures/array/model/arraySlice";
import { ArrayItem } from "./ArrayItem";
type ArrayBracketProps = {
  argType: ArgumentType;
  side?: "left" | "right";
};
const ArrayBracket: React.FC<ArrayBracketProps> = t0 => {
  const $ = _c(9);
  const {
    argType,
    side: t1
  } = t0;
  const side = t1 === undefined ? "left" : t1;
  const theme = useTheme();
  if (argType === ArgumentType.SET) {
    const char = side === "left" ? "<" : ">";
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        display: "inline-block",
        height: 37,
        opacity: 0.6,
        transform: "scale(0.8, 2)"
      };
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== char) {
      t3 = <Typography component="span" className="array-bracket" color="primary.light" fontSize={26} fontWeight={200} sx={t2}>{char}</Typography>;
      $[1] = char;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  if (argType === ArgumentType.STRING) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Typography>"</Typography>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const borderStyle = `2px solid ${theme.palette.primary.light}`;
  const t2 = side === "left" ? "-12px" : 0;
  const t3 = side === "left" ? 0 : "-12px";
  const t4 = side === "left" ? "none" : "rotate(180deg)";
  let t5;
  if ($[4] !== borderStyle || $[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <Box className="array-bracket" sx={{
      width: 12,
      height: 42,
      marginRight: t2,
      marginLeft: t3,
      borderRadius: "2px 0 0 2px",
      borderLeft: borderStyle,
      borderTop: borderStyle,
      borderBottom: borderStyle,
      opacity: 0.6,
      transition: "opacity 0.1s",
      zIndex: 10,
      pointerEvents: "none",
      transform: t4
    }} />;
    $[4] = borderStyle;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export type ArrayStructureViewProps = {
  data: ArrayData;
  parentColorMap?: ArrayData["colorMap"];
  sx?: SxProps;
};
export const ArrayStructureView: React.FC<ArrayStructureViewProps> = t0 => {
  const $ = _c(17);
  const {
    data,
    parentColorMap
  } = t0;
  let t1;
  if ($[0] !== data.nodes) {
    t1 = arrayDataItemSelectors.selectAll(data.nodes);
    $[0] = data.nodes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  const {
    argType
  } = data;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: "fit-content",
      zIndex: 10,
      "&:hover": {
        ".array-bracket": {
          opacity: 1
        },
        ".array-item": {
          "&::before": {
            opacity: 1
          }
        }
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== argType) {
    t3 = <ArrayBracket argType={argType} />;
    $[3] = argType;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data.colorMap || $[6] !== items || $[7] !== parentColorMap) {
    t4 = items.length > 0 ? items.map(item => <ArrayItem key={item.id} colorMap={data.colorMap ?? parentColorMap} item={item} />) : <Box sx={{
      width: 32,
      height: 12
    }} />;
    $[5] = data.colorMap;
    $[6] = items;
    $[7] = parentColorMap;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Stack direction="row">{t4}</Stack>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== argType) {
    t6 = <ArrayBracket argType={argType} side="right" />;
    $[11] = argType;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Stack direction="row" sx={t2}>{t3}{t5}{t6}</Stack>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};